// The app's entry page at "/" - replaces the blueprint's generic "Applications" placeholder
// (see bluesrints/Applications.tsx) with a PIH-branded landing page: a headline CTA into the
// catalog, and a small news feed editable via public/landing-news.json (no redeploy needed for
// a content-only change, no backend involved - this isn't STIBO/catalog domain data).
//
// It also warms the catalog's own TanStack Query cache (index/tree/config, same query keys
// CatalogPage uses) by calling those hooks here too, so by the time the user clicks through to
// /catalog, that data is very likely already cached instead of loading from scratch.

import { Card, Loader, Stack, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { BaseLayout } from '@uag/react-core';

import { brand } from './branding';
import { FitToViewport } from './catalog/components/FitToViewport';
import { HeaderBrand } from './catalog/components/HeaderBrand';
import { HeaderWishlist } from './catalog/components/HeaderWishlist';
import { LanguageSwitch } from './catalog/components/LanguageSwitch';
import { MobileHeaderBar } from './catalog/components/MobileHeaderBar';
import { SearchMask } from './catalog/components/SearchMask';
import { useCatalogLabels } from './catalog/labels';
import { DATA_LANGUAGE, useCatalogConfig, useCatalogIndex, useTree } from './catalog/queries';
import { loc, type Localized } from './catalog/types';

interface LandingNewsItem {
    id: string;
    date: string;
    title: Localized;
    body: Localized;
}

const NEWS_URL = `${import.meta.env.BASE_URL}landing-news.json`;

const useLandingNews = () =>
    useQuery({
        queryKey: ['landing', 'news'],
        queryFn: async () => {
            const res = await fetch(NEWS_URL);
            if (!res.ok) {
                return [] as LandingNewsItem[];
            }
            return (await res.json()) as LandingNewsItem[];
        },
        staleTime: Infinity,
    });

export const LandingPage = () => {
    const { locale, L } = useCatalogLabels();

    // Warms the catalog's own TanStack Query cache (see top-of-file comment) - the results
    // themselves aren't rendered here anymore (article count and the preset CTA button were
    // removed from the landing page).
    useCatalogIndex(DATA_LANGUAGE);
    useTree();
    useCatalogConfig();
    const newsQuery = useLandingNews();

    const news = [...(newsQuery.data ?? [])].sort((a, b) => b.date.localeCompare(a.date));

    return (
        <BaseLayout versionNumber={APP_VERSION}>
            <LanguageSwitch />
            <HeaderWishlist />
            <HeaderBrand />
            <MobileHeaderBar showTitle />
            <FitToViewport>
                <Stack gap="xl">
                    <SearchMask />

                    <Stack gap="md">
                        <Title order={3}>{L.landingNews}</Title>
                        {newsQuery.isLoading ? (
                            <Loader size="sm" />
                        ) : news.length === 0 ? (
                            <Text c="dimmed">{L.landingNoNews}</Text>
                        ) : (
                            // A feed - date, headline, text stacked underneath each other - not the
                            // former title/date-in-a-row-then-body layout, which read as a lopsided
                            // two-column table on wide screens and didn't stack sensibly on mobile.
                            <Stack gap="lg">
                                {news.map((item) => (
                                    <Card
                                        key={item.id}
                                        padding="lg"
                                        radius="md"
                                        shadow="xs"
                                        styles={{ root: { backgroundColor: '#fff' } }}
                                        withBorder
                                    >
                                        <Stack gap={4}>
                                            <Text c="dimmed" size="sm">
                                                {item.date}
                                            </Text>
                                            <Text c={brand.primary} fw={600} size="lg">
                                                {loc(item.title, locale, item.id)}
                                            </Text>
                                            <Text c="dimmed" size="sm">
                                                {loc(item.body, locale)}
                                            </Text>
                                        </Stack>
                                    </Card>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            </FitToViewport>
        </BaseLayout>
    );
};
